import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, Cell,
} from 'recharts';
import {
  Package, Truck, RotateCcw, IndianRupee, AlertTriangle, Clock, Store,
  Scale, PackageCheck, XCircle, BarChart3, ArrowRight, Wallet, FileText, Download,
} from 'lucide-react';
import { api } from '@/api/client';
import { moneyCompact } from '@/utils/money';
import { KpiValueSkeleton } from '@/components/ui/Skeleton';

type OT = 'ALL' | 'B2C' | 'B2B';
type Trend = { day: string; total: number; delivered: number; rto: number };
type Status = { statusId: number; label: string; count: number };
type Zone = { zoneCode: string; label: string; count: number };
type CourierRow = { courierId: number; name: string; assigned: number; delivered: number; rto: number; inTransit: number; deliveryPct: number };
type MerchantRow = { merchantId: number; name: string; shipments: number; delivered: number; rto: number; deliveryPct: number; revenue: number; cod: number };
type Admin = {
  range: { from: string; to: string };
  orderType: OT;
  cards: {
    totalShipment: number; todayShipment: number; delivered: number; todayDelivered: number;
    rto: number; todayRto: number; inTransit: number; pending: number; failed: number;
    cancelled: number; lost: number; ndrPending: number; weightOpen: number; pickupPending: number;
    activeMerchants: number; deliveryRate: number; bookedDelivered: number; revenue: number; codValue: number;
  };
  split: { b2c: number; b2b: number };
  trend: Trend[];
  statusWise: Status[];
  zoneWise: Zone[];
  topCouriers: CourierRow[];
  topMerchants: MerchantRow[];
  ndr: { total: number; pending: number; resolved: number };
  finance: {
    invoiced: number; invoiceCount: number; paid: number; unpaid: number;
    creditNotes: number; creditNoteCount: number; debitNotes: number; debitNoteCount: number;
    codCollected: number; codRemitted: number; codPending: number;
  };
};

const PALETTE = ['#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6', '#ec4899', '#6366f1', '#84cc16', '#f97316'];
const num = (n: number) => Number(n ?? 0).toLocaleString('en-IN');
const money = (n: number) => '₹' + Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
// Headline tiles use the compact form — `₹1,26,81,709` overflows its card and reads as noise, where
// `₹1.27 Cr` is the actual message. The exact figure is kept on the element's `title` so hovering
// still gives the precise number; the TABLES below stay exact, because those are read to be acted on.
const moneyTile = (n: number) => moneyCompact(n);
const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

export function AdminDashboardPage() {
  const [ot, setOt] = useState<OT>('ALL');
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(daysAgo(0));

  const { data, isLoading } = useQuery<Admin>({
    queryKey: ['admin-dashboard', ot, from, to],
    queryFn: async () => (await api.get<Admin>('/dashboard/admin', { params: { orderType: ot, from, to } })).data,
    staleTime: 30_000,
  });
  const c = data?.cards;
  const f = data?.finance;

  function exportCsv() {
    if (!data) return;
    const esc = (v: any) => { const s = String(v ?? ''); return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const L: string[] = [];
    L.push(`Operations Dashboard export`, `Scope,${ot}`, `From,${from}`, `To,${to}`, '');
    L.push('Summary,Value');
    const cc = data.cards;
    ([
      ['Total Shipments', cc.totalShipment], ['Today Shipments', cc.todayShipment],
      ['Delivered in period', cc.delivered], ['Delivered (of booked in period)', cc.bookedDelivered], ['Delivery Rate %', cc.deliveryRate], ['RTO delivered in period', cc.rto],
      ['In Transit', cc.inTransit], ['Pending', cc.pending], ['Booking Failed', cc.failed],
      ['Cancelled', cc.cancelled], ['NDR Pending', cc.ndrPending], ['Weight Disputes', cc.weightOpen],
      ['Active Merchants', cc.activeMerchants], ['Revenue', cc.revenue], ['COD Value', cc.codValue],
    ] as [string, any][]).forEach(([k, v]) => L.push(`${esc(k)},${esc(v)}`));
    L.push('', 'B2C vs B2B,Count', `B2C,${data.split.b2c}`, `B2B,${data.split.b2b}`);
    L.push('', 'Status,Count'); (data.statusWise ?? []).forEach((s) => L.push(`${esc(s.label)},${s.count}`));
    L.push('', 'Zone,Count'); (data.zoneWise ?? []).forEach((z) => L.push(`${esc(z.label)},${z.count}`));
    L.push('', 'Top Couriers,Assigned,Delivered,RTO,Delivery %');
    (data.topCouriers ?? []).forEach((c2) => L.push(`${esc(c2.name)},${c2.assigned},${c2.delivered},${c2.rto},${c2.deliveryPct}`));
    L.push('', 'Top Merchants,Shipments,Delivered,Revenue,COD');
    (data.topMerchants ?? []).forEach((m) => L.push(`${esc(m.name)},${m.shipments},${m.delivered},${m.revenue},${m.cod}`));
    L.push('', 'NDR,Value', `Total,${data.ndr?.total ?? 0}`, `Action Taken,${data.ndr?.resolved ?? 0}`, `Pending,${data.ndr?.pending ?? 0}`);
    L.push('', 'Finance,Value',
      `Invoiced,${f?.invoiced ?? 0}`, `Outstanding,${f?.unpaid ?? 0}`,
      `COD Collected,${f?.codCollected ?? 0}`, `COD Pending,${f?.codPending ?? 0}`);
    const blob = new Blob([L.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `dashboard-${ot}-${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 size={22} className="text-primary" /> Operations Dashboard
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Company-wide view across B2C &amp; B2B — orders, delivery, NDR, COD and revenue.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} disabled={!data} title="Export dashboard counts & summary (CSV)"
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
            <Download size={15} /> Export
          </button>
          <Link to="/reports" className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 transition">
            <FileText size={15} /> Module Reports <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {(['ALL', 'B2C', 'B2B'] as OT[]).map((t) => (
            <button key={t} onClick={() => setOt(t)}
              className={`px-5 py-1.5 text-sm rounded-md transition ${ot === t ? 'bg-white shadow-sm text-primary font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
              {t === 'ALL' ? 'All' : t}
            </button>
          ))}
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-0.5">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-0.5">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
        </div>
        <div className="flex gap-1">
          {[
            { l: 'Last 30 days', from: 29, to: 0 },
            { l: 'Last 7 days', from: 6, to: 0 },
            { l: 'Yesterday', from: 1, to: 1 },
            { l: 'Today', from: 0, to: 0 },
          ].map((p) => {
            const active = from === daysAgo(p.from) && to === daysAgo(p.to);
            return (
              <button key={p.l} onClick={() => { setFrom(daysAgo(p.from)); setTo(daysAgo(p.to)); }}
                className={`text-xs px-2 py-1.5 border rounded transition ${active ? 'bg-primary text-white border-primary' : 'text-slate-600 hover:bg-slate-50'}`}>{p.l}</button>
            );
          })}
        </div>
      </div>

      {/* Primary KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <BigCard icon={<Package size={18} />} tone="blue" label="Total Shipments" value={num(c?.totalShipment ?? 0)} sub={`${num(c?.todayShipment ?? 0)} today`} loading={isLoading} />
        {/* Counts deliveries that HAPPENED in the selected period (by delivery date), not shipments
            booked in it — so a "Yesterday" view shows yesterday's deliveries rather than 0. The
            delivery-% underneath is the booked cohort, which is the only honest basis for a rate. */}
        <BigCard icon={<PackageCheck size={18} />} tone="emerald" label="Delivered in period" value={num(c?.delivered ?? 0)}
          sub={`${num(c?.bookedDelivered ?? 0)} of ${num(c?.totalShipment ?? 0)} booked (${c?.deliveryRate ?? 0}%)`} loading={isLoading} />
        <BigCard icon={<RotateCcw size={18} />} tone="rose" label="RTO delivered in period" value={num(c?.rto ?? 0)} sub={`${num(c?.todayRto ?? 0)} today`} loading={isLoading} />
        <BigCard icon={<IndianRupee size={18} />} tone="violet" label="Revenue"
          value={moneyTile(c?.revenue ?? 0)} exact={money(c?.revenue ?? 0)}
          sub={`${moneyTile(c?.codValue ?? 0)} COD value`} subExact={`${money(c?.codValue ?? 0)} COD value`}
          loading={isLoading} />
      </div>

      {/* Secondary stat chips */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Chip icon={<Truck size={14} />} label="In Transit" value={num(c?.inTransit ?? 0)} tone="text-sky-700" />
        <Chip icon={<Clock size={14} />} label="Pending" value={num(c?.pending ?? 0)} tone="text-slate-700" />
        <Chip icon={<AlertTriangle size={14} />} label="NDR Pending" value={num(c?.ndrPending ?? 0)} tone="text-amber-700" />
        <Chip icon={<XCircle size={14} />} label="Booking Failed" value={num(c?.failed ?? 0)} tone="text-rose-700" />
        <Chip icon={<Scale size={14} />} label="Weight Disputes" value={num(c?.weightOpen ?? 0)} tone="text-fuchsia-700" />
        <Chip icon={<Store size={14} />} label="Active Merchants" value={num(c?.activeMerchants ?? 0)} tone="text-emerald-700" />
      </div>

      {/* Trend + split */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <Panel title="Daily Trend — Booked vs Delivered vs RTO" className="xl:col-span-2">
          {(data?.trend?.length ?? 0) === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data!.trend} margin={{ left: 4, right: 16, top: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => String(d).slice(5)} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={38} />
                <Tooltip formatter={(v: any) => num(Number(v))} />
                <Legend />
                <Line type="monotone" dataKey="total" name="Booked" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="delivered" name="Delivered" stroke="#10b981" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="rto" name="RTO" stroke="#ef4444" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Panel>
        <Panel title="B2C vs B2B Split">
          {((data?.split?.b2c ?? 0) + (data?.split?.b2b ?? 0)) === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={[{ name: 'B2C', value: data!.split.b2c }, { name: 'B2B', value: data!.split.b2b }]}
                  dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2} label>
                  <Cell fill="#0ea5e9" /><Cell fill="#8b5cf6" />
                </Pie>
                <Tooltip formatter={(v: any) => num(Number(v))} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      {/* Status + Zone bars */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
        <Panel title="Shipments by Status">
          <MiniBar rows={(data?.statusWise ?? []).slice(0, 10).map((s) => ({ label: s.label, value: s.count }))} />
        </Panel>
        <Panel title="Shipments by Zone">
          <MiniBar rows={(data?.zoneWise ?? []).map((z) => ({ label: z.label, value: z.count }))} />
        </Panel>
      </div>

      {/* Top couriers + merchants */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
        <Panel title="Top Couriers">
          <SimpleTable
            head={['Courier', 'Assigned', 'Delivered', 'RTO', 'Del %']}
            align={['left', 'right', 'right', 'right', 'right']}
            rows={(data?.topCouriers ?? []).map((c2) => [c2.name, num(c2.assigned), num(c2.delivered), num(c2.rto), `${c2.deliveryPct}%`])}
          />
        </Panel>
        <Panel title="Top Merchants">
          <SimpleTable
            head={['Merchant', 'Shipments', 'Delivered', 'Revenue', 'COD']}
            align={['left', 'right', 'right', 'right', 'right']}
            rows={(data?.topMerchants ?? []).map((m) => [m.name, num(m.shipments), num(m.delivered), money(m.revenue), money(m.cod)])}
          />
        </Panel>
      </div>

      {/* NDR + Finance snapshot */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="NDR Snapshot" icon={<AlertTriangle size={16} className="text-amber-500" />}>
          <div className="grid grid-cols-3 gap-3">
            <MiniStat label="Total NDR" value={num(data?.ndr?.total ?? 0)} tone="text-slate-900" />
            <MiniStat label="Action Taken" value={num(data?.ndr?.resolved ?? 0)} tone="text-emerald-700" />
            <MiniStat label="Pending" value={num(data?.ndr?.pending ?? 0)} tone="text-rose-700" />
          </div>
          <Link to="/reports?module=ndr" className="inline-flex items-center gap-1 text-xs text-primary mt-3 hover:underline">
            Full NDR report <ArrowRight size={12} />
          </Link>
        </Panel>
        <Panel title="Finance Snapshot" icon={<Wallet size={16} className="text-violet-500" />}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MiniStat label="Invoiced" value={moneyTile(f?.invoiced ?? 0)} exact={money(f?.invoiced ?? 0)} tone="text-violet-700" />
            <MiniStat label="Outstanding" value={moneyTile(f?.unpaid ?? 0)} exact={money(f?.unpaid ?? 0)} tone="text-rose-700" />
            <MiniStat label="COD Collected" value={moneyTile(f?.codCollected ?? 0)} exact={money(f?.codCollected ?? 0)} tone="text-sky-700" />
            <MiniStat label="COD Pending" value={moneyTile(f?.codPending ?? 0)} exact={money(f?.codPending ?? 0)} tone="text-amber-700" />
          </div>
          <Link to="/reports?module=finance" className="inline-flex items-center gap-1 text-xs text-primary mt-3 hover:underline">
            Full finance report <ArrowRight size={12} />
          </Link>
        </Panel>
      </div>
    </div>
  );
}

/* ── building blocks ─────────────────────────────────────────────── */

function BigCard({ icon, label, value, exact, sub, subExact, tone, loading }: { icon: React.ReactNode; label: string; value: string; exact?: string; sub: string; subExact?: string; tone: 'blue' | 'emerald' | 'rose' | 'violet'; loading?: boolean }) {
  const tones: Record<string, string> = { blue: 'bg-blue-50 text-blue-700', emerald: 'bg-emerald-50 text-emerald-700', rose: 'bg-rose-50 text-rose-700', violet: 'bg-violet-50 text-violet-700' };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">{label}</div>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tones[tone]}`}>{icon}</div>
      </div>
      {loading ? <KpiValueSkeleton /> : (
        <>
          <div className="text-2xl font-bold text-slate-900 mt-1" title={exact}>{value}</div>
          <div className="text-xs text-slate-400 mt-0.5" title={subExact}>{sub}</div>
        </>
      )}
    </div>
  );
}

function Chip({ icon, label, value, tone }: { icon: React.ReactNode; label: string; value: string; tone: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[11px] text-slate-500">{icon}{label}</div>
      <div className={`text-lg font-bold mt-0.5 ${tone}`}>{value}</div>
    </div>
  );
}

function Panel({ title, icon, children, className = '' }: { title: string; icon?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl p-4 ${className}`}>
      <h3 className="font-semibold text-slate-800 text-sm mb-3 flex items-center gap-1.5">{icon}{title}</h3>
      {children}
    </div>
  );
}

function MiniStat({ label, value, exact, tone }: { label: string; value: string; exact?: string; tone: string }) {
  return (
    <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-lg font-bold ${tone}`} title={exact}>{value}</div>
    </div>
  );
}

function Empty() { return <div className="h-[280px] flex items-center justify-center text-slate-400 text-sm">No data for this range</div>; }

function MiniBar({ rows }: { rows: { label: string; value: number }[] }) {
  const view = rows.filter((r) => r.value > 0);
  if (!view.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height={Math.max(240, view.length * 30)}>
      <BarChart data={view} layout="vertical" margin={{ left: 8, right: 20, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v: any) => num(Number(v))} />
        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
          {view.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function SimpleTable({ head, rows, align }: { head: string[]; rows: (string | number)[][]; align: ('left' | 'right')[] }) {
  if (!rows.length) return <div className="text-center py-8 text-slate-400 text-sm">No data</div>;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>{head.map((h, i) => <th key={h} className={`px-3 py-2 ${align[i] === 'right' ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="border-t border-slate-100">
              {r.map((cell, ci) => (
                <td key={ci} className={`px-3 py-2 ${align[ci] === 'right' ? 'text-right tabular-nums' : 'text-left'} ${ci === 0 ? 'text-slate-700 truncate max-w-[220px]' : 'text-slate-600'}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
